// Firebase service for managing match data

// Wait for Firebase to be available
const waitForFirebase = () => {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    const maxAttempts = 200; // 20 seconds max
    
    const checkFirebase = () => {
      if (window.firebaseDb) {
        console.log('✅ Firebase initialized successfully');
        resolve(window.firebaseDb);
      } else if (attempts >= maxAttempts) {
        console.error('❌ Firebase initialization timeout after', attempts * 100, 'ms');
        reject(new Error('Firebase initialization timeout'));
      } else {
        attempts++;
        if (attempts % 50 === 0) {
          console.log('⏳ Waiting for Firebase... (' + (attempts * 100) + 'ms)');
        }
        setTimeout(checkFirebase, 100);
      }
    };
    checkFirebase();
  });
};

// Import Firestore functions dynamically
let firestoreFunctions = null;

const getFirestoreFunctions = async () => {
  if (!firestoreFunctions) {
    const { collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc, addDoc, onSnapshot, query, orderBy, where } = await import('https://www.gstatic.com/firebasejs/12.1.0/firebase-firestore.js');
    firestoreFunctions = { collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc, addDoc, onSnapshot, query, orderBy, where };
  }
  return firestoreFunctions;
};

// Collection names
const MATCHES_COLLECTION = 'matches';
const CONFIG_COLLECTION = 'configurations';
const CONFIG_DOC_ID = 'app_config';
const POSTS_COLLECTION = 'posts';

// Firebase service class
class FirebaseService {
  constructor() {
    this.db = null;
    this.initialized = false;
  }

  async init() {
    if (!this.initialized) {
      this.db = await waitForFirebase();
      this.initialized = true;
    }
    return this.db;
  }

  // Get all matches
  async getAllMatches() {
    try {
      console.log('FirebaseService.getAllMatches: Starting...');
      await this.init();
      console.log('FirebaseService.getAllMatches: Firebase initialized');
      
      const { collection, getDocs, query, orderBy } = await getFirestoreFunctions();
      console.log('FirebaseService.getAllMatches: Firestore functions loaded');
      
      const matchesRef = collection(this.db, MATCHES_COLLECTION);
      const q = query(matchesRef, orderBy('date', 'desc'));
      console.log('FirebaseService.getAllMatches: Querying collection...');
      
      const querySnapshot = await getDocs(q);
      console.log('FirebaseService.getAllMatches: Query complete, snapshot size:', querySnapshot.size);
      
      const matches = [];
      querySnapshot.forEach((doc) => {
        matches.push({ id: doc.id, ...doc.data() });
      });
      
      console.log('FirebaseService: ✅ Successfully fetched', matches.length, 'matches from Firestore');
      console.log('FirebaseService: Sample match:', matches[0]);
      return matches;
    } catch (error) {
      console.error('❌ FirebaseService Error getting matches from Firestore:', error);
      console.error('Error details:', error.message, error.code);
      console.log('FirebaseService: Falling back to localStorage');
      
      // Fallback to localStorage
      const localMatches = this.getLocalStorageMatches();
      console.log('FirebaseService: Found', localMatches.length, 'matches in localStorage');
      return localMatches;
    }
  }

  // Add a new match
  async addMatch(matchData) {
    try {
      await this.init();
      const { collection, addDoc } = await getFirestoreFunctions();
      
      const matchesRef = collection(this.db, MATCHES_COLLECTION);
      const docRef = await addDoc(matchesRef, {
        ...matchData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      return { id: docRef.id, ...matchData };
    } catch (error) {
      console.error('Error adding match:', error);
      // Fallback to localStorage
      return this.addLocalStorageMatch(matchData);
    }
  }

  // Update a match
  async updateMatch(matchId, updateData) {
    try {
      await this.init();
      const { doc, updateDoc } = await getFirestoreFunctions();
      
      const matchRef = doc(this.db, MATCHES_COLLECTION, matchId);
      await updateDoc(matchRef, {
        ...updateData,
        updatedAt: new Date().toISOString()
      });
      
      return { id: matchId, ...updateData };
    } catch (error) {
      console.error('Error updating match:', error);
      // Fallback to localStorage
      return this.updateLocalStorageMatch(matchId, updateData);
    }
  }

  // Delete a match
  async deleteMatch(matchId) {
    try {
      await this.init();
      const { doc, deleteDoc } = await getFirestoreFunctions();
      
      const matchRef = doc(this.db, MATCHES_COLLECTION, matchId);
      await deleteDoc(matchRef);
      
      return true;
    } catch (error) {
      console.error('Error deleting match:', error);
      // Fallback to localStorage
      return this.deleteLocalStorageMatch(matchId);
    }
  }

  // Listen to real-time updates
  async subscribeToMatches(callback) {
    if (typeof callback !== 'function') {
      console.error('subscribeToMatches: callback must be a function');
      return () => {}; // Return empty cleanup function
    }
    
    try {
      await this.init();
      
      if (!this.db) {
        throw new Error('Firebase database not initialized');
      }
      
      const { collection, onSnapshot, query, orderBy } = await getFirestoreFunctions();
      
      const matchesRef = collection(this.db, MATCHES_COLLECTION);
      const q = query(matchesRef, orderBy('date', 'desc'));
      
      console.log('FirebaseService: Setting up real-time listener for matches');
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        try {
          const matches = [];
          querySnapshot.forEach((doc) => {
            matches.push({ id: doc.id, ...doc.data() });
          });
          console.log('FirebaseService: Real-time update received -', matches.length, 'matches');
          callback(matches);
        } catch (error) {
          console.error('Error processing snapshot:', error);
        }
      }, (error) => {
        console.error('Snapshot error:', error);
        console.log('FirebaseService: Falling back to localStorage polling');
        // Fallback to localStorage
        callback(this.getLocalStorageMatches());
      });
      
      return unsubscribe;
    } catch (error) {
      console.error('Error subscribing to matches:', error);
      console.log('FirebaseService: Using localStorage polling fallback');
      
      // Initial callback with localStorage data
      try {
        const initialMatches = this.getLocalStorageMatches();
        console.log('FirebaseService: Initial localStorage data -', initialMatches.length, 'matches');
        callback(initialMatches);
      } catch (err) {
        console.error('Error getting initial localStorage data:', err);
      }
      
      // Fallback to localStorage with polling
      const interval = setInterval(() => {
        try {
          const matches = this.getLocalStorageMatches();
          callback(matches);
        } catch (error) {
          console.error('Error in fallback polling:', error);
        }
      }, 5000);
      
      return () => {
        clearInterval(interval);
      };
    }
  }

  // Migrate localStorage data to Firebase
  async migrateLocalStorageToFirebase() {
    try {
      const localMatches = this.getLocalStorageMatches();
      if (localMatches.length > 0) {
        console.log('Migrating', localMatches.length, 'matches to Firebase...');
        
        for (const match of localMatches) {
          // Remove the local ID and let Firebase generate a new one
          const { id, ...matchData } = match;
          await this.addMatch(matchData);
        }
        
        console.log('Migration completed successfully!');
        // Clear localStorage after successful migration
        localStorage.removeItem('ntf_admin_matches');
      }
    } catch (error) {
      console.error('Error migrating data:', error);
    }
  }

  // Fallback localStorage methods
  getLocalStorageMatches() {
    const savedMatches = localStorage.getItem('ntf_admin_matches');
    return savedMatches ? JSON.parse(savedMatches) : [];
  }

  addLocalStorageMatch(matchData) {
    const matches = this.getLocalStorageMatches();
    const newMatch = { ...matchData, id: Date.now() };
    matches.push(newMatch);
    localStorage.setItem('ntf_admin_matches', JSON.stringify(matches));
    return newMatch;
  }

  updateLocalStorageMatch(matchId, updateData) {
    const matches = this.getLocalStorageMatches();
    const index = matches.findIndex(m => m.id === matchId);
    if (index !== -1) {
      matches[index] = { ...matches[index], ...updateData };
      localStorage.setItem('ntf_admin_matches', JSON.stringify(matches));
    }
    return { id: matchId, ...updateData };
  }

  deleteLocalStorageMatch(matchId) {
    const matches = this.getLocalStorageMatches();
    const filteredMatches = matches.filter(m => m.id !== matchId);
    localStorage.setItem('ntf_admin_matches', JSON.stringify(filteredMatches));
    return true;
  }

  // Configuration Management Methods

  // Get global configuration (leagues, clubs, predictions)
  async getConfiguration() {
    try {
      await this.init();
      console.log('firebaseService.getConfiguration: Firebase initialized');
      
      const { doc, getDoc } = await getFirestoreFunctions();
      console.log('firebaseService.getConfiguration: Firestore functions loaded');
      
      const configRef = doc(this.db, CONFIG_COLLECTION, CONFIG_DOC_ID);
      console.log('firebaseService.getConfiguration: Fetching document...');
      
      const configDoc = await getDoc(configRef);
      
      if (configDoc.exists()) {
        console.log('✅ firebaseService.getConfiguration: Document found, returning data:', {
          leagues: configDoc.data().leagues?.length || 0,
          clubs: Object.keys(configDoc.data().clubs || {}).length,
          categories: Object.keys(configDoc.data().predictionCategories || {}).length,
          timestamp: configDoc.data().updatedAt
        });
        return configDoc.data();
      } else {
        console.log('firebaseService.getConfiguration: Document does not exist');
        return null;
      }
    } catch (error) {
      console.error('❌ firebaseService.getConfiguration: Error:', error);
      return null;
    }
  }

  // Save global configuration
  async saveConfiguration(config) {
    try {
      await this.init();
      console.log('firebaseService.saveConfiguration: Firebase initialized');
      
      const { doc, setDoc } = await getFirestoreFunctions();
      console.log('firebaseService.saveConfiguration: Firestore functions loaded');
      
      const configRef = doc(this.db, CONFIG_COLLECTION, CONFIG_DOC_ID);
      console.log('firebaseService.saveConfiguration: Config reference created:', CONFIG_COLLECTION, CONFIG_DOC_ID);
      
      const dataToSave = {
        ...config,
        updatedAt: new Date().toISOString()
      };
      console.log('firebaseService.saveConfiguration: Saving data...', {
        leagues: dataToSave.leagues?.length || 0,
        clubs: Object.keys(dataToSave.clubs || {}).length,
        categories: Object.keys(dataToSave.predictionCategories || {}).length,
        timestamp: dataToSave.updatedAt
      });
      
      await setDoc(configRef, dataToSave);
      console.log('✅ firebaseService.saveConfiguration: Successfully saved to Firestore');
      
      return true;
    } catch (error) {
      console.error('❌ firebaseService.saveConfiguration: Error:', error);
      console.error('Error details:', error.message, error.code, error.domain);
      return false;
    }
  }

  // Update leagues
  async updateLeagues(leagues) {
    try {
      console.log('firebaseService.updateLeagues: Fetching current config...');
      const config = await this.getConfiguration() || {};
      console.log('firebaseService.updateLeagues: Current config fetched, updating leagues...', {
        currentLeagues: config.leagues?.length || 0,
        newLeagues: leagues.length
      });
      
      config.leagues = leagues;
      console.log('firebaseService.updateLeagues: Calling saveConfiguration...');
      const result = await this.saveConfiguration(config);
      
      if (result) {
        console.log('✅ firebaseService.updateLeagues: Leagues updated successfully');
      } else {
        console.error('❌ firebaseService.updateLeagues: Failed to save');
      }
      
      return result;
    } catch (error) {
      console.error('❌ firebaseService.updateLeagues: Error occurred:', error);
      return false;
    }
  }

  // Update clubs
  async updateClubs(clubs) {
    try {
      console.log('firebaseService.updateClubs: Fetching current config...');
      const config = await this.getConfiguration() || {};
      console.log('firebaseService.updateClubs: Current config fetched, updating clubs...', {
        currentClubsCount: Object.keys(config.clubs || {}).length,
        newClubsCount: Object.keys(clubs || {}).length
      });
      
      config.clubs = clubs;
      console.log('firebaseService.updateClubs: Calling saveConfiguration...');
      const result = await this.saveConfiguration(config);
      
      if (result) {
        console.log('✅ firebaseService.updateClubs: Clubs updated successfully');
      } else {
        console.error('❌ firebaseService.updateClubs: Failed to save');
      }
      
      return result;
    } catch (error) {
      console.error('❌ firebaseService.updateClubs: Error occurred:', error);
      return false;
    }
  }

  // Update tournaments
  async updateTournaments(tournaments) {
    try {
      console.log('firebaseService.updateTournaments: Fetching current config...');
      const config = await this.getConfiguration() || {};
      
      const tournamentKeys = Object.keys(tournaments || {});
      const totalTeams = tournamentKeys.reduce((sum, key) => 
        sum + (tournaments[key]?.teams?.length || 0), 0
      );
      
      console.log('firebaseService.updateTournaments: Current config fetched, updating tournaments...', {
        tournamentsCount: tournamentKeys.length,
        totalTeams: totalTeams
      });
      
      config.tournaments = tournaments;
      console.log('firebaseService.updateTournaments: Calling saveConfiguration...');
      const result = await this.saveConfiguration(config);
      
      if (result) {
        console.log('✅ firebaseService.updateTournaments: Tournaments updated successfully');
      } else {
        console.error('❌ firebaseService.updateTournaments: Failed to save');
      }
      
      return result;
    } catch (error) {
      console.error('❌ firebaseService.updateTournaments: Error occurred:', error);
      return false;
    }
  }

  // Update prediction categories
  async updatePredictionCategories(categories) {
    try {
      const config = await this.getConfiguration() || {};
      config.predictionCategories = categories;
      return await this.saveConfiguration(config);
    } catch (error) {
      console.error('Error updating prediction categories:', error);
      return false;
    }
  }

  // Subscribe to configuration changes
  async subscribeToConfiguration(callback) {
    if (typeof callback !== 'function') {
      console.error('subscribeToConfiguration: callback must be a function');
      return () => {};
    }
    
    try {
      console.log('firebaseService.subscribeToConfiguration: Initializing subscription...');
      await this.init();
      const { doc, onSnapshot } = await getFirestoreFunctions();
      
      const configRef = doc(this.db, CONFIG_COLLECTION, CONFIG_DOC_ID);
      console.log('firebaseService.subscribeToConfiguration: Setting up snapshot listener...');
      
      const unsubscribe = onSnapshot(configRef, (docSnapshot) => {
        try {
          if (docSnapshot.exists()) {
            console.log('✅ firebaseService.subscribeToConfiguration: Config changed, calling callback with:', {
              leagues: docSnapshot.data().leagues?.length || 0,
              clubs: Object.keys(docSnapshot.data().clubs || {}).length,
              categories: Object.keys(docSnapshot.data().predictionCategories || {}).length,
              timestamp: docSnapshot.data().updatedAt
            });
            callback(docSnapshot.data());
          } else {
            console.log('firebaseService.subscribeToConfiguration: Config document does not exist');
            callback(null);
          }
        } catch (error) {
          console.error('Error processing config snapshot:', error);
        }
      }, (error) => {
        console.error('❌ firebaseService.subscribeToConfiguration: Snapshot error:', error);
      });
      
      console.log('✅ firebaseService.subscribeToConfiguration: Listener set up successfully');
      return unsubscribe;
    } catch (error) {
      console.error('❌ firebaseService.subscribeToConfiguration: Error setting up subscription:', error);
      return () => {};
    }
  }

  // ==================== POSTS METHODS ====================

  // Get all posts
  async getAllPosts() {
    try {
      console.log('FirebaseService.getAllPosts: Starting...');
      await this.init();
      
      const { collection, getDocs, query, orderBy } = await getFirestoreFunctions();
      const postsRef = collection(this.db, POSTS_COLLECTION);
      const q = query(postsRef, orderBy('createdAt', 'desc'));
      
      const querySnapshot = await getDocs(q);
      console.log('FirebaseService.getAllPosts: Found', querySnapshot.size, 'posts');
      
      const posts = [];
      querySnapshot.forEach((doc) => {
        posts.push({ id: doc.id, ...doc.data() });
      });
      
      return posts;
    } catch (error) {
      console.error('Error getting posts:', error);
      return [];
    }
  }

  // Add a new post
  async addPost(postData) {
    try {
      await this.init();
      const { collection, addDoc } = await getFirestoreFunctions();
      
      const postsRef = collection(this.db, POSTS_COLLECTION);
      const post = {
        ...postData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        published: postData.published || false
      };
      
      const docRef = await addDoc(postsRef, post);
      console.log('✅ Post added with ID:', docRef.id);
      
      return { id: docRef.id, ...post };
    } catch (error) {
      console.error('❌ Error adding post:', error);
      return null;
    }
  }

  // Update a post
  async updatePost(postId, updateData) {
    try {
      await this.init();
      const { doc, updateDoc } = await getFirestoreFunctions();
      
      const postRef = doc(this.db, POSTS_COLLECTION, postId);
      const updates = {
        ...updateData,
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(postRef, updates);
      console.log('✅ Post updated:', postId);
      
      return true;
    } catch (error) {
      console.error('❌ Error updating post:', error);
      return false;
    }
  }

  // Delete a post
  async deletePost(postId) {
    try {
      await this.init();
      const { doc, deleteDoc } = await getFirestoreFunctions();
      
      const postRef = doc(this.db, POSTS_COLLECTION, postId);
      await deleteDoc(postRef);
      console.log('✅ Post deleted:', postId);
      
      return true;
    } catch (error) {
      console.error('❌ Error deleting post:', error);
      return false;
    }
  }

  // Subscribe to posts changes
  async subscribeToPosts(callback) {
    if (typeof callback !== 'function') {
      console.error('subscribeToPosts: callback must be a function');
      return () => {};
    }
    
    try {
      await this.init();
      const { collection, onSnapshot, query, orderBy } = await getFirestoreFunctions();
      
      const postsRef = collection(this.db, POSTS_COLLECTION);
      const q = query(postsRef, orderBy('createdAt', 'desc'));
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        try {
          const posts = [];
          querySnapshot.forEach((doc) => {
            posts.push({ id: doc.id, ...doc.data() });
          });
          console.log('Posts subscription update:', posts.length, 'posts');
          callback(posts);
        } catch (error) {
          console.error('Error processing posts snapshot:', error);
        }
      }, (error) => {
        console.error('Posts snapshot error:', error);
        callback([]);
      });
      
      return unsubscribe;
    } catch (error) {
      console.error('Error subscribing to posts:', error);
      return () => {};
    }
  }
}

// Create and export a singleton instance
const firebaseService = new FirebaseService();
export default firebaseService;